import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import { useCryptoMajorsInstruments } from "../../hooks/api/admin/useCryptoMajorsInstruments";
import {
  Actions,
  CardHeader,
  CardSubtitle,
  CardTitle,
  Chip,
  Chips,
  ErrorText,
  Label,
  LabelHint,
  Summary,
  SummaryLabel,
  SummaryValue,
  Textarea,
  ToolPanel,
} from "./style";

// Split input on commas, spaces, or newlines so admins can paste in any
// common format. Uppercased and deduped client-side; the backend re-validates
// against the live instruments service and rejects unknown symbols.
const parseSymbols = (raw: string): string[] => {
  const tokens = raw
    .split(/[\s,]+/)
    .map((t) => t.trim().toUpperCase())
    .filter((t) => t.length > 0);
  return tokens.filter((t, i) => tokens.indexOf(t) === i);
};

const arraysEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
  return true;
};

export function CryptoMajorsInstruments() {
  const { data, isLoading, updateCryptoMajorsInstruments } =
    useCryptoMajorsInstruments();

  const [text, setText] = useState<string>("");

  // Reset textarea whenever fresh data arrives from the server.
  useEffect(() => {
    if (!data) return;
    setText(data.instruments.join(", "));
  }, [data]);

  const parsed = useMemo(() => parseSymbols(text), [text]);

  const hasInvalidShape = useMemo(
    // Perpetuals on this exchange follow ASSET-PERP. Reject obvious typos
    // (no dash, lowercase that didn't survive uppercasing) before sending.
    () => parsed.some((s) => !/^[A-Z0-9]+-PERP$/.test(s)),
    [parsed]
  );

  const isEmpty = parsed.length === 0;

  const dirty = useMemo(() => {
    if (!data) return false;
    return !arraysEqual(parsed, data.instruments);
  }, [data, parsed]);

  const onSave = useCallback(async () => {
    if (isEmpty || hasInvalidShape) return;
    const confirmed = window.confirm(
      `Replace crypto majors instruments with:\n\n${parsed.join(", ")}\n\n(${
        parsed.length
      } symbols)\n\nThis takes effect immediately on the public weekly-leaderboard endpoint.`
    );
    if (!confirmed) return;
    await updateCryptoMajorsInstruments(parsed);
  }, [hasInvalidShape, isEmpty, parsed, updateCryptoMajorsInstruments]);

  const onReset = useCallback(() => {
    if (!data) return;
    setText(data.instruments.join(", "));
  }, [data]);

  return (
    <ToolPanel role="tabpanel">
      <CardHeader>
        <CardTitle>Crypto Majors Instruments</CardTitle>
        <CardSubtitle>
          Curated perp instrument names that count toward the{" "}
          <strong>Crypto Majors</strong> weekly leaderboard slice. Replaces the
          list wholesale. Backend validates that every name is a registered
          perpetual, so typos are rejected with an error message. Takes effect
          immediately.
        </CardSubtitle>
      </CardHeader>

      <Label htmlFor="crypto-majors-instruments">
        Instrument names
        <LabelHint>
          Comma, space, or newline separated. e.g. BTC-PERP, ETH-PERP, SOL-PERP
        </LabelHint>
      </Label>
      <Textarea
        id="crypto-majors-instruments"
        hasErrors={hasInvalidShape}
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={isLoading}
        placeholder="BTC-PERP, ETH-PERP, SOL-PERP, ..."
      />
      {hasInvalidShape && (
        <ErrorText>
          Each entry must look like <code>ASSET-PERP</code> (uppercase, ending
          in -PERP).
        </ErrorText>
      )}

      <Summary>
        <SummaryLabel>Parsed ({parsed.length} symbols)</SummaryLabel>
        <SummaryValue valid={!isEmpty && !hasInvalidShape}>
          {(() => {
            if (isEmpty) return "Empty";
            if (hasInvalidShape) return "Invalid";
            return "OK";
          })()}
        </SummaryValue>
      </Summary>

      {parsed.length > 0 && (
        <Chips>
          {parsed.map((s) => (
            <Chip key={s}>{s}</Chip>
          ))}
        </Chips>
      )}

      <Actions>
        <Button
          buttonTheme={ButtonThemeEnum.NEUTRAL2}
          onClick={onReset}
          disabled={isLoading || !dirty}
        >
          Reset
        </Button>
        <Button
          buttonTheme={ButtonThemeEnum.PRIMARY}
          onClick={onSave}
          disabled={isLoading || isEmpty || hasInvalidShape || !dirty}
        >
          Save
        </Button>
      </Actions>
    </ToolPanel>
  );
}
